/**
 * ServiceCardSkeleton Component
 * Loading placeholder for service cards in the catalog
 * Matches layout and dimensions of ServiceCard component exactly
 *
 * Uses shimmer animation instead of pulse for modern loading UX
 */

import { Skeleton, SkeletonText } from '../common/Skeleton';

interface ServiceCardSkeletonProps {
  showImage?: boolean;
  isPopular?: boolean;
}

export function ServiceCardSkeleton({ showImage = false, isPopular = false }: ServiceCardSkeletonProps) {
  return (
    <div
      className={`
        relative bg-white border-2 rounded-lg shadow-sm
        ${isPopular ? 'border-primary-500' : 'border-gray-200'}
      `}
      aria-label="Loading service card"
      role="status"
      aria-busy="true"
      aria-live="polite"
    >
      {/* Popular badge skeleton */}
      {isPopular && (
        <div className="absolute top-0 right-0 z-10">
          <Skeleton className="h-6 w-24 rounded-bl-lg rounded-tr-lg" />
        </div>
      )}

      {/* Optional image skeleton - matches ServiceCard image dimensions */}
      {showImage && (
        <Skeleton className="w-full h-32 rounded-none rounded-t-lg" />
      )}

      {/* Card content - exact dimensions matching ServiceCard p-6 padding */}
      <div className="p-6">
        {/* Container size skeleton - h-8 matches text-2xl */}
        <Skeleton className="h-8 w-24 mb-2" />

        {/* Description skeleton - 2 lines matching min-h-[3rem] from ServiceCard */}
        <SkeletonText lines={2} className="mb-4 min-h-[3rem]" />

        {/* Pricing skeleton - matches text-lg and text-xs alignment */}
        <div className="mb-4 flex items-baseline gap-1">
          <Skeleton className="h-6 w-20" />
          <Skeleton className="h-3 w-16" />
        </div>

        {/* CTA Button skeleton - matches min-h-[44px] from ServiceCard button */}
        <Skeleton className="h-12 w-full rounded-md" />
      </div>

      {/* Visual divider for popular card */}
      {isPopular && <Skeleton className="h-1 w-full rounded-none rounded-b-lg" />}
    </div>
  );
}
